import React from 'react';
import { Input } from '../ui/input';

/**
 * Fill-in-the-Blank field — renders a template string with inline inputs.
 *
 * The template uses `___` (three underscores) OR `{blank}` as placeholders for
 * each blank.  Blank values are stored as an array indexed by position:
 *   template = "The patient's name is ___ born on ___ weighing ___ kg"
 *   response = ["John Doe", "1990-01-15", "75"]
 *
 * Props:
 *   template  — the prompt with `___` or `{blank}` placeholders
 *   value     — array of current blank values (defaults to [])
 *   onChange  — (newArray) => void. Called when any blank changes.
 *   readOnly  — when true, blanks render as underlined static text (view mode)
 *   testIdPrefix — prefix for data-testid on inputs
 */
export default function FillBlankField({
  template = '',
  value = [],
  onChange,
  readOnly = false,
  testIdPrefix = 'fib',
}) {
  // Split the template on `___` or `{blank}` placeholders (both supported).
  const segments = React.useMemo(() => splitTemplate(template), [template]);

  const updateBlank = (idx, v) => {
    const next = Array.isArray(value) ? [...value] : [];
    // Pad with empties if the user skipped earlier blanks
    while (next.length <= idx) next.push('');
    next[idx] = v;
    onChange(next);
  };

  return (
    <div className="text-[15px] leading-[2.1] text-[#0A2342]" data-testid={testIdPrefix}>
      {segments.map((seg, i) => {
        if (seg.type === 'text') {
          return <span key={i} className="whitespace-pre-wrap">{seg.value}</span>;
        }
        const val = value?.[seg.index] || '';
        if (readOnly) {
          // Rendered view: value shown underlined, or a visible blank slot if empty
          return (
            <span
              key={i}
              className="inline-block min-w-[90px] px-1 mx-0.5 border-b-2 border-[#0E7490] text-[#0A2342] font-medium"
              data-testid={`${testIdPrefix}-value-${seg.index}`}
            >
              {val || <span className="text-[#94A3B8]">______</span>}
            </span>
          );
        }
        // Edit mode: inline input that looks like a blank line
        return (
          <Input
            key={i}
            value={val}
            onChange={(e) => updateBlank(seg.index, e.target.value)}
            className="inline-block w-[140px] mx-1 border-0 border-b-2 border-[#0E7490] rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-[#14B8A6] px-1 h-7 text-[15px]"
            data-testid={`${testIdPrefix}-blank-${seg.index}`}
          />
        );
      })}
    </div>
  );
}

// Split template into an array of { type: 'text', value } and { type: 'blank', index }
export function splitTemplate(template) {
  const out = [];
  const rx = /(___+|\{blank\})/gi;
  let last = 0;
  let match;
  let idx = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = rx.exec(template)) !== null) {
    if (match.index > last) {
      out.push({ type: 'text', value: template.slice(last, match.index) });
    }
    out.push({ type: 'blank', index: idx });
    idx += 1;
    last = match.index + match[0].length;
  }
  if (last < template.length) {
    out.push({ type: 'text', value: template.slice(last) });
  }
  return out;
}

// Count the number of blanks in a template
export function countBlanks(template) {
  return (template.match(/(___+|\{blank\})/gi) || []).length;
}
